import type { PermissionAction } from './types';

/**
 * Privilege ladder (lower number = stronger). edit and create share level 2.
 * approve is outside the ladder: it implies view only, not create/edit/delete/admin.
 */
export const PERMISSION_LADDER_LEVEL = {
  admin: 0,
  delete: 1,
  edit: 2,
  create: 2,
  view: 5,
} as const;

export type LadderPermissionAction = keyof typeof PERMISSION_LADDER_LEVEL;

const LADDER_ACTION_SET = new Set<string>(Object.keys(PERMISSION_LADDER_LEVEL));

function isLadderAction(action: PermissionAction): action is LadderPermissionAction {
  return LADDER_ACTION_SET.has(action);
}

function ladderLevel(action: LadderPermissionAction): number {
  return PERMISSION_LADDER_LEVEL[action];
}

/**
 * True when `held` is strong enough to perform `required`.
 * - Ladder: any held action with level <= required level (e.g. edit satisfies view).
 * - approve: only satisfies view and approve checks; never create/edit/delete.
 * - admin: only satisfies admin check (not used to bypass approve-only flows).
 */
export function satisfiesAction(held: PermissionAction[], required: PermissionAction): boolean {
  if (!held.length) {
    return false;
  }

  if (required === 'admin') {
    return held.includes('admin');
  }

  if (required === 'approve') {
    return held.includes('approve') || held.includes('admin');
  }

  if (required === 'view') {
    if (held.includes('approve')) {
      return true;
    }
    return held.some(
      (h) => isLadderAction(h) && ladderLevel(h) <= PERMISSION_LADDER_LEVEL.view
    );
  }

  if (!isLadderAction(required)) {
    return false;
  }

  const requiredLevel = ladderLevel(required);
  return held.some((h) => isLadderAction(h) && ladderLevel(h) <= requiredLevel);
}

/** Any access to the resource (at least view-level). */
export function hasAnyResourceAccess(held: PermissionAction[]): boolean {
  return satisfiesAction(held, 'view');
}
